import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { completePlay, shareCompletion } from "./gamesApi";

/**
 * Optional "Share to Community" after a level is completed. Never posts on
 * its own: the member asks to share, the server verifies the completion and
 * proposes the post, the member reviews (and may edit the caption), then
 * posts. One in-flight request at a time; the server creates at most one
 * post per completion, so retries are safe. Trivia passes its completion
 * (already verified by the server when the level ended) instead of a proof;
 * its score is added to the post only if the member ticks the box. The
 * game's thumbnail is shown read-only: the server attaches it to the post.
 */
function GameShare({ token, proof, completion: verified }) {
  const [step, setStep] = useState("offer");
  const [completion, setCompletion] = useState(null);
  const [caption, setCaption] = useState("");
  const [error, setError] = useState("");
  const [alreadyShared, setAlreadyShared] = useState(false);
  const [includeScore, setIncludeScore] = useState(false);
  const busyRef = useRef(false);

  if (step === "dismissed") return null;

  const verify = () => {
    if (busyRef.current) return;
    if (verified) {
      setCompletion(verified);
      setCaption(verified.headline);
      setAlreadyShared(!!verified.shared);
      setStep(verified.shared ? "shared" : "review");
      return;
    }
    busyRef.current = true;
    setStep("verifying");
    setError("");
    completePlay(token, proof).then((result) => {
      busyRef.current = false;
      if (result.ok && result.data.completion) {
        setCompletion(result.data.completion);
        setCaption(result.data.completion.headline);
        setStep(result.data.completion.shared ? "shared" : "review");
        setAlreadyShared(!!result.data.completion.shared);
      } else {
        setError(result.error);
        setStep("verify_error");
      }
    });
  };

  const post = () => {
    if (busyRef.current || !completion) return;
    busyRef.current = true;
    setStep("posting");
    setError("");
    shareCompletion(completion.id, caption, includeScore && !!completion.score).then((result) => {
      busyRef.current = false;
      if (result.ok) {
        setAlreadyShared(!!result.data.already_shared);
        setStep("shared");
      } else {
        setError(result.error);
        setStep("post_error");
      }
    });
  };

  if (!token) {
    return (
      <p className="games-share__note" data-testid="games-share-unavailable">
        Sharing to Community isn’t available for this round.
      </p>
    );
  }

  if (step === "offer") {
    return (
      <div className="games-share" data-testid="games-share">
        <button type="button" className="btn btn-outline-secondary games-share__button" onClick={verify}>
          Share to Community
        </button>
        <button type="button" className="btn btn-link games-share__dismiss" onClick={() => setStep("dismissed")}>
          Not now
        </button>
      </div>
    );
  }

  if (step === "verifying") {
    return (
      <p className="games-share__note" role="status" aria-live="polite">
        Checking your completion…
      </p>
    );
  }

  if (step === "verify_error") {
    return (
      <div className="games-share games-share--error" role="alert">
        <p className="games-share__note">{error}</p>
        <button type="button" className="btn btn-outline-secondary games-share__button" onClick={verify}>
          Try again
        </button>
        <button type="button" className="btn btn-link games-share__dismiss" onClick={() => setStep("dismissed")}>
          Not now
        </button>
      </div>
    );
  }

  if (step === "shared") {
    return (
      <div className="games-share games-share--done" role="status" aria-live="polite">
        <p className="games-share__note">
          {alreadyShared ? "This completion was already shared to Community." : "Shared to Community!"}{" "}
          <Link to="/community/social">View the Community feed</Link>
        </p>
      </div>
    );
  }

  const posting = step === "posting";
  return (
    <form
      className="games-share games-share--review"
      onSubmit={(e) => {
        e.preventDefault();
        post();
      }}
      aria-labelledby="games-share-title"
    >
      <h3 id="games-share-title" className="games-share__title">
        Review your Community post
      </h3>
      {completion.thumbnail_url ? (
        <img
          className="games-share__thumb"
          src={completion.thumbnail_url}
          alt={completion.game_title + " thumbnail (included with your post)"}
          loading="lazy"
          data-testid="games-share-thumb"
        />
      ) : null}
      <label className="games-share__label" htmlFor="games-share-caption">
        Caption
      </label>
      <textarea
        id="games-share-caption"
        className="form-control games-share__caption"
        rows={2}
        maxLength={completion ? completion.caption_max : 500}
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        disabled={posting}
      />
      {completion.score ? (
        <label className="games-share__score">
          <input
            type="checkbox"
            checked={includeScore}
            onChange={(e) => setIncludeScore(e.target.checked)}
            disabled={posting}
          />{" "}
          Include my score ({completion.score.correct}/{completion.score.total})
        </label>
      ) : null}
      <p className="games-share__fixed">
        {completion.type_label} · {completion.game_title} · Level {completion.level_number}
        {completion.score && includeScore
          ? " · Score " + completion.score.correct + "/" + completion.score.total + " (" + completion.score.percentage + "%)"
          : ""}
        :{" "}
        <span className="games-share__link">{completion.link}</span>
      </p>
      <p className="games-share__hint">
        Posted publicly to the Community feed under your handle. Community posting rules apply.
      </p>
      {step === "post_error" ? (
        <p className="games-share__error" role="alert">
          {error} Your post was not published.
        </p>
      ) : null}
      <div className="games-share__actions">
        <button type="submit" className="btn btn-danger" disabled={posting}>
          {posting ? "Posting…" : step === "post_error" ? "Try again" : "Post to Community"}
        </button>
        <button
          type="button"
          className="btn btn-link games-share__dismiss"
          onClick={() => setStep("dismissed")}
          disabled={posting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default GameShare;
